import { useEffect, useRef, useState } from "react";

interface Point3D {
  x: number;
  y: number;
  z: number;
}

interface Connection {
  start: Point3D;
  end: Point3D;
}

export function Globe3D() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };
    
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let rotation = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener("resize", resize);

    // Generate sphere points
    const generateSpherePoints = (count: number): Point3D[] => {
      const points: Point3D[] = [];
      const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle

      for (let i = 0; i < count; i++) {
        const y = 1 - (i / (count - 1)) * 2;
        const radius = Math.sqrt(1 - y * y);
        const theta = phi * i;

        points.push({
          x: Math.cos(theta) * radius,
          y: y,
          z: Math.sin(theta) * radius,
        });
      }

      return points;
    };

    // Generate connections between nearby points
    const generateConnections = (points: Point3D[]): Connection[] => {
      const connections: Connection[] = [];
      const maxDist = 0.5;

      for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
          const dx = points[i].x - points[j].x;
          const dy = points[i].y - points[j].y;
          const dz = points[i].z - points[j].z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

          if (dist < maxDist && Math.random() > 0.7) {
            connections.push({ start: points[i], end: points[j] });
          }
        }
      }

      return connections;
    };

    const points = generateSpherePoints(80);
    const connections = generateConnections(points);

    // Rotate point around Y axis
    const rotateY = (point: Point3D, angle: number): Point3D => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return {
        x: point.x * cos - point.z * sin,
        y: point.y,
        z: point.x * sin + point.z * cos,
      };
    };

    // Project 3D to 2D
    const project = (point: Point3D, centerX: number, centerY: number, radius: number) => {
      const perspective = 2;
      const scale = perspective / (perspective + point.z);
      return {
        x: centerX + point.x * radius * scale,
        y: centerY + point.y * radius * scale,
        scale,
        z: point.z,
      };
    };

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.min(width, height) * 0.35;

      ctx.clearRect(0, 0, width, height);

      // Draw connections
      ctx.strokeStyle = "hsla(43, 70%, 47%, 0.15)";
      ctx.lineWidth = 1;

      connections.forEach((conn) => {
        const start = project(rotateY(conn.start, rotation), centerX, centerY, radius);
        const end = project(rotateY(conn.end, rotation), centerX, centerY, radius);

        if (start.z > -0.3 && end.z > -0.3) {
          const alpha = Math.min(start.scale, end.scale) * 0.3;
          ctx.strokeStyle = `hsla(43, 70%, 47%, ${alpha})`;
          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();
        }
      });

      // Draw points
      points.forEach((point) => {
        const rotated = rotateY(point, rotation);
        const projected = project(rotated, centerX, centerY, radius);

        if (projected.z > -0.5) {
          const alpha = (projected.z + 1) / 2;
          const size = projected.scale * 3;

          ctx.beginPath();
          ctx.arc(projected.x, projected.y, size, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(43, 70%, 55%, ${alpha * 0.8})`;
          ctx.fill();
        }
      });

      // Slow rotation
      if (!prefersReducedMotion) {
        rotation += 0.003;
      }

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationId);
    };
  }, [prefersReducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  );
}
